import BigNumber from 'bignumber.js';
import { ActionCableService, sharePublishReplay, swalloError } from 'ngx-libs';
import { BehaviorSubject, combineLatest, Observable, of, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, mergeMap, startWith, switchMap, take, tap } from 'rxjs/operators';
import { AccountService } from '../services/account.service';
import { ContractService } from '../services/contract.service';
import { EthWalletService } from '../services/eth-wallet.service';
import { Vault } from './vault/vault';
import { Token } from './token';
import { SpotPricer } from './spot-pricer';
import { Pool } from './pool';
import { Config } from './config';
import { MainService } from '../services/main.service';
import { OptionPricer } from './option-pricer';
import { Vault as ContractVault } from './contracts/vault';
import { keepUpdater } from '../rxjs';
import { Expiry } from './expiry';
import PoolAbi from 'src/app/abi/pool';
import SignedPoolAbi from 'src/app/abi/signed-pool';
import TradingVaultAbi from 'src/app/abi/trading-vault';
import { TradingVault } from './trading-vault';

export const DECIMALS18 = new BigNumber(10).pow(18);

export class Market {
  private poolsCache: Map<string, Pool> = new Map();
  private tradingVaultsCache: Map<string, TradingVault> = new Map();
  private expiriesCache: Map<number, Expiry> = new Map();

  name: string;
  icon: string;
  api = this.mainService.markets.of('');
  vault: Vault;
  quote: Token;
  spotPricer: SpotPricer;
  optionPricer: OptionPricer;
  config: Config;
  expiries: Observable<Expiry[]>;
  pools: Observable<Pool[]>;
  tradingVaults: Observable<TradingVault[]>;
  poolsOrVaults: Observable<(Pool | TradingVault)[]>;
  totalLiquidity: Observable<BigNumber>;

  contractVault = new ContractVault();
  signedData: Observable<any>;
  shoppingCart: any[] = [];
  domain: any;
  expanded = true;

  adjustUpdater = new BehaviorSubject(null);

  constructor(
    private market: any,
    private networkInfo: any,
    private accountService: AccountService,
    private ethWalletService: EthWalletService,
    private contractService: ContractService,
    private actionCableService: ActionCableService,
    private mainService: MainService
  ) {
    this.name = this.market.name;
    this.icon = `/retired-zksync/assets/images/${this.name.split('-')[0].toLocaleLowerCase()}.svg`;
    this.api = this.mainService.markets.of(this.name);

    this.domain = {
      name: 'SignatureValidator',
      version: '1',
      chainId: this.networkInfo.params.chainId,
      verifyingContract: this.market.signatureValidator
    };

    this.quote = new Token(this.market.quote, 6, this.accountService, this.ethWalletService, this.contractService);

    this.spotPricer = new SpotPricer(
      this.market.spotPricer,
      this.accountService,
      this.contractService,
      this.ethWalletService,
      this.actionCableService,
      this
    );

    this.vault = new Vault(
      this.market.vault,
      this.accountService,
      this.contractService,
      this.ethWalletService,
      this.actionCableService,
      this.mainService,
      this
    );

    this.optionPricer = new OptionPricer(this.networkInfo.contracts.optionPricer, this.accountService, this.contractService, this.ethWalletService, this);

    this.config = new Config(
      this.market.config,
      this.accountService,
      this.contractService,
      this.ethWalletService,
      this.actionCableService,
      this.mainService,
      this
    );

    const received = this.actionCableService
      .received({
        channel: 'ExpiriesChannel'
      })
      .pipe(startWith(0), debounceTime(10));

    this.expiries = combineLatest([received, timer(0, 60000)]).pipe(
      debounceTime(20),
      keepUpdater(),
      switchMap(() => this.api.expiries.query().pipe(swalloError())),
      map((json) => json.expiries),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map((reocrds) => reocrds.map((r: any) => this.processExpiry(r))),
      sharePublishReplay(1)
    );

    const poolsReceived = this.actionCableService
      .received({
        channel: 'PoolsChannel'
      })
      .pipe(startWith(0), debounceTime(10));

    this.pools = combineLatest([poolsReceived, timer(0, 60000)]).pipe(
      debounceTime(20),
      keepUpdater(),
      switchMap(() => this.api.pools.query().pipe(swalloError())),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map((json) => json.pools.map((p: any) => this.processPool(p))),
      tap((pools) => {
        this.contractVault.config.pools = pools.map((p: Pool) => p.contract);
      }),
      sharePublishReplay(1)
    );

    this.totalLiquidity = this.pools.pipe(
      switchMap((pools) => combineLatest(pools.map((p) => p.available))),
      map((values: any[]) => values.reduce((p, v) => p.plus(v), new BigNumber(0)))
    );

    const tradingVaultsReceived = this.actionCableService
      .received({
        channel: 'TradingVaultsChannel'
      })
      .pipe(startWith(0), debounceTime(10));

    this.tradingVaults = combineLatest([tradingVaultsReceived, timer(0, 60000)]).pipe(
      debounceTime(20),
      keepUpdater(),
      switchMap(() => this.api.tradingVaults.query().pipe(swalloError())),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map((json) => json.tradingVaults.map((p: any) => this.processTradingVault(p))),
      sharePublishReplay(1)
    );

    this.poolsOrVaults = combineLatest([this.pools, this.tradingVaults]).pipe(
      map(([p, t]) => [...p, ...t])
    );

    const signedData = new BehaviorSubject(null);
    this.signedData = signedData.pipe(
      mergeMap((v) => {
        if (v) {
          return of(v);
        } else {
          return this.api.oracles.getSignedData().pipe(
            tap((d) => {
              signedData.next(d);
              setTimeout(() => {
                signedData.next(null);
              }, 25000);
            })
          )
        }
      }),
      filter((v) => v)
    );

    try {
      this.shoppingCart = JSON.parse(localStorage.getItem('MULTI_TRADE')) || [];
    } catch (e) {
      this.shoppingCart = [];
    }
  }

  getPoolById(id: number) {
    return this.pools.pipe(
      take(1),
      switchMap((pools) => {
        const pool = pools.find((p) => p.id === id);
        if (pool) {
          return of(pool);
        } else {
          return this.api
            .pools.get(id)
            .pipe(map((json) => this.processPool(json.pool)));
        }
      })
    );
  }

  getExpiry(timestamp: number) {
    return this.expiries.pipe(
      take(1),
      switchMap((expiries) => {
        const expiry = expiries.find((p) => p.timestamp === timestamp);
        if (expiry) {
          return of(expiry);
        } else {
          return of(this.getExpiryCache(timestamp, false));
        }
      })
    );
  }

  addShoppingCart(expiry: number, strike: any, isCall: boolean, amount: any, acceptableTotal: any) {
    this.shoppingCart.push([expiry, strike.toString(10), isCall, amount.toString(10), acceptableTotal.toString(10)]);
    localStorage.setItem('MULTI_TRADE', JSON.stringify(this.shoppingCart));
  }

  clearShoppingCart() {
    this.shoppingCart = [];
    localStorage.setItem('MULTI_TRADE', JSON.stringify(this.shoppingCart));
  }

  submitShoppingCart() {
    const trades = this.shoppingCart.map((t) => {
      return {
        expiry: t[0],
        strike: new BigNumber(t[1]),
        isCall: t[2],
        amount: new BigNumber(t[3]),
        acceptableTotal: new BigNumber(t[4])
      };
    });
    return this.vault.signTrade(trades).pipe(
      tap(() => this.clearShoppingCart())
    );
  }

  private processExpiry(record: any) {
    const expiry = this.getExpiryCache(record.timestamp);
    expiry.updateData(record);
    return expiry;
  }

  private getExpiryCache(expiry: number, active = true) {
    if (!this.expiriesCache.has(expiry)) {
      this.expiriesCache.set(
        expiry,
        new Expiry(expiry, this.ethWalletService, this.actionCableService, this.mainService, this)
      );
      if (!active) {
        this.api
          .expiries.get(expiry)
          .subscribe((json) => {
            this.expiriesCache.get(expiry).updateData(json.expiry);
          });
      }
    }
    return this.expiriesCache.get(expiry);
  }

  private getPool(pool: string, type: string) {
    if (!this.poolsCache.has(pool)) {
      const abi = type === 'v1' ? PoolAbi : SignedPoolAbi;
      const p = new Pool(
        pool,
        abi,
        this.accountService,
        this.contractService,
        this.ethWalletService,
        this.actionCableService,
        this.mainService,
        this
      );
      p.type = type;
      this.poolsCache.set(pool, p);
    }
    return this.poolsCache.get(pool);
  }

  private processPool(record: any) {
    const pool = this.getPool(record.address, record.type);
    pool.id = record.id;
    pool.name = record.name;
    pool.description = record.description;
    pool.apy = record.apy;
    pool.avgApy = record.avgApy;
    pool.createdAt = record.createdAt;
    pool.profit30D = record.avgApy / 12;
    pool.updateData(record);
    return pool;
  }

  private getTradingVault(address: string) {
    if (!this.tradingVaultsCache.has(address)) {
      const tradingVault = new TradingVault(
        address,
        TradingVaultAbi,
        this.accountService,
        this.contractService,
        this.ethWalletService,
        this.actionCableService,
        this.mainService,
        this
      );
      this.tradingVaultsCache.set(address, tradingVault);
    }
    return this.tradingVaultsCache.get(address);
  }

  private processTradingVault(record: any) {
    const tradingVault = this.getTradingVault(record.address);
    tradingVault.updateData(record);
    return tradingVault;
  }
}
